import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Upload,
  ShieldCheck,
  Layers,
  Truck,
  Cpu,
  Network,
  Globe,
  Zap,
  CheckCircle2,
  FileText,
  Settings2,
  Boxes,
  PackageSearch,
  Workflow,
  FileSearch,
  UserCheck,
  Gauge,
  Target,
  Scale,
  Send,
  ClipboardCheck,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { trackCtaClick } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { HOME_COPY } from "./copy";
import Reveal from "@/components/Reveal";

/* ──────────────────────────────────────────────────────────────
   Switzh — homepage redesign (dark, AI-native infrastructure feel)
   Aesthetic: navy/black gradient, electric-blue accents, subtle
   tech grid, glass cards. Matches /ask-switzh visual language.
   ────────────────────────────────────────────────────────────── */

/** Shared dark-section wrapper. */
const DarkSection = ({
  id,
  className,
  children,
  ambient = true,
}: {
  id?: string;
  className?: string;
  children: React.ReactNode;
  ambient?: boolean;
}) => (
  <section
    id={id}
    className={cn(
      "relative overflow-hidden bg-[#06080F] text-white",
      className,
    )}
  >
    {/* Tech grid floor */}
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0 opacity-[0.18]",
        ambient && "ambient-grid",
      )}
      style={{
        backgroundImage:
          "linear-gradient(to right, rgba(96,165,250,0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(96,165,250,0.12) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
        WebkitMaskImage:
          "radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)",
        maskImage:
          "radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)",
      }}
    />
    {/* Electric blue glow */}
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute -top-40 left-1/2 -z-0 h-[520px] w-[820px] -translate-x-1/2 rounded-full",
        ambient && "ambient-glow",
      )}
      style={{
        background:
          "radial-gradient(closest-side, rgba(43,99,246,0.35), rgba(43,99,246,0) 70%)",
        filter: "blur(28px)",
      }}
    />
    <div className="relative">{children}</div>
  </section>
);

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#7DA8FF] backdrop-blur">
    <span className="h-1.5 w-1.5 rounded-full bg-[#2B63F6] shadow-[0_0_10px_rgba(43,99,246,0.9)]" />
    {children}
  </div>
);

const Chip = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-xs font-medium text-white/80 backdrop-blur">
    <span className="h-1.5 w-1.5 rounded-full bg-[#2B63F6]" />
    {children}
  </span>
);

/* ─────────────────────────  HERO  ───────────────────────── */

const Hero = ({ c, localePath }: { c: ReturnType<typeof useCopy>; localePath: (p: string) => string }) => (
  <DarkSection className="border-b border-white/10">
    <div className="container py-12 md:py-24 lg:py-28">
      <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
        {/* Left: copy */}
        <div className="hero-mobile-safe lg:col-span-7">
          <div className="enter-up"><Eyebrow>{c.heroEyebrow}</Eyebrow></div>
          <h1 className="enter-up stagger-1 mt-5 text-[32px] font-semibold leading-[1.08] tracking-[-0.02em] text-white sm:text-5xl lg:text-[60px]">
            {c.heroH1A}{" "}
            <span className="bg-gradient-to-r from-[#7DA8FF] via-[#4F86FF] to-[#2B63F6] bg-clip-text text-transparent pb-1 inline-block">
              {c.heroH1B}
            </span>
          </h1>
          <p className="enter-up stagger-2 mt-5 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
            {c.heroSub}
          </p>

          <div className="enter-up stagger-3 mt-7 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
            <Button
              asChild
              size="lg"
              className="group cta-glow h-12 bg-[#2B63F6] font-semibold text-white shadow-[0_8px_30px_-8px_rgba(43,99,246,0.7)] hover:bg-[#1F56E0]"
            >
              <Link
                to={localePath("/ask-switzh")}
                onClick={() => trackCtaClick("home_hero", "ask_switzh")}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                {c.ctaAsk}
                <ArrowRight className="cta-arrow ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="group h-12 border-white/15 bg-white/[0.04] font-semibold text-white transition-colors hover:border-white/25 hover:bg-white/[0.08] hover:text-white"
            >
              <Link
                to={localePath("/request-quote")}
                onClick={() => trackCtaClick("home_hero", "submit_rfq")}
              >
                <Upload className="mr-2 h-4 w-4" />
                {c.ctaRfq}
              </Link>
            </Button>
            <Link
              to={localePath("/supplier-partners")}
              onClick={() => trackCtaClick("home_hero", "supplier_partners")}
              className="cta-arrow-host inline-flex h-12 items-center justify-center text-sm font-medium text-white/70 underline-offset-4 transition-colors hover:text-white hover:underline sm:px-2"
            >
              {c.ctaSupplier} <ArrowRight className="cta-arrow ml-1 h-3.5 w-3.5" />
            </Link>
          </div>

          <p className="enter-up stagger-4 mt-6 max-w-2xl text-[13px] leading-relaxed text-white/55 sm:text-sm">
            <ShieldCheck className="mr-1.5 -mt-0.5 inline h-4 w-4 text-[#7DA8FF]" />
            {c.heroTrust}
          </p>

          <div className="enter-up stagger-5 mt-6 flex flex-wrap gap-2">
            {c.chips.map((chip) => (
              <Chip key={chip}>{chip}</Chip>
            ))}
          </div>
        </div>

        {/* Right: Ask Switzh preview card */}
        <div className="hero-mobile-safe enter-right lg:col-span-5">
          <AskSwitzhCard c={c} localePath={localePath} />
        </div>
      </div>
    </div>
  </DarkSection>
);

const AskSwitzhCard = ({
  c,
  localePath,
}: {
  c: ReturnType<typeof useCopy>;
  localePath: (p: string) => string;
}) => {
  const flowIcons = [Send, FileSearch, ClipboardCheck, Scale, Truck];
  return (
    <div className="relative">
      <div
        aria-hidden
        className="absolute -inset-2 rounded-2xl bg-gradient-to-br from-[#2B63F6]/30 via-transparent to-[#7DA8FF]/20 blur-xl"
      />
      <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-5 shadow-2xl backdrop-blur-xl sm:p-6">
        {/* Card chrome */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#7DA8FF]">
            <Sparkles className="h-3.5 w-3.5" />
            {c.cardEyebrow}
          </div>
          <div className="flex gap-1">
            <span className="h-2 w-2 rounded-full bg-white/20" />
            <span className="h-2 w-2 rounded-full bg-white/20" />
            <span className="h-2 w-2 rounded-full bg-[#2B63F6]" />
          </div>
        </div>

        <h2 className="mt-4 text-base font-semibold text-white sm:text-lg">
          {c.cardTitle}
        </h2>
        <p className="mt-1 text-xs leading-relaxed text-white/55">
          {c.cardSub}
        </p>

        {/* Vertical workflow */}
        <ol className="mt-4 space-y-2">
          {c.heroFlow.map((label, i) => {
            const Icon = flowIcons[i] ?? Network;
            const isLast = i === c.heroFlow.length - 1;
            return (
              <li key={label} className="relative">
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0B0F1A] px-3 py-2">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#2B63F6]/15 text-[#7DA8FF] ring-1 ring-inset ring-[#2B63F6]/30 sm:h-8 sm:w-8">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40">
                      Step {i + 1}
                    </div>
                    <div className="text-sm font-medium text-white/90">
                      {label}
                    </div>
                  </div>
                  {isLast && (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-[#7DA8FF]" />
                  )}
                </div>
                {!isLast && (
                  <div
                    aria-hidden
                    className="ml-[15px] h-2.5 w-px bg-gradient-to-b from-[#2B63F6]/60 to-transparent"
                  />
                )}
              </li>
            );
          })}
        </ol>

        <Button
          asChild
          className="group cta-glow mt-5 h-11 w-full bg-[#2B63F6] font-semibold text-white hover:bg-[#1F56E0]"
        >
          <Link
            to={localePath("/ask-switzh")}
            onClick={() => trackCtaClick("home_hero_card", "ask_switzh")}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            {c.cardCta}
            <ArrowRight className="cta-arrow ml-2 h-4 w-4" />
          </Link>
        </Button>
        <p className="mt-3 text-center text-[11px] leading-relaxed text-white/45">
          {c.cardFoot}
        </p>
      </div>
    </div>
  );
};

/* ─────────────────  SECTION 2: WHAT TO EXPECT  ───────────────── */

const SectionHeader = ({
  eyebrow,
  title,
  sub,
  align = "center",
}: {
  eyebrow: string;
  title: string;
  sub?: string;
  align?: "center" | "left";
}) => (
  <div
    className={cn(
      "mx-auto max-w-3xl",
      align === "center" ? "text-center" : "text-left",
    )}
  >
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className="mt-4 text-[26px] font-semibold leading-[1.15] tracking-[-0.02em] text-white sm:text-4xl lg:text-5xl">
      {title}
    </h2>
    {sub && (
      <p className="mt-3 text-[15px] leading-relaxed text-white/65 sm:mt-4 sm:text-lg">
        {sub}
      </p>
    )}
  </div>
);

const GlassCard = ({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) => (
  <div className="group card-premium relative rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur sm:p-6">
    <div className="card-icon flex h-11 w-11 items-center justify-center rounded-lg bg-[#2B63F6]/15 text-[#7DA8FF] ring-1 ring-inset ring-[#2B63F6]/30">
      {icon}
    </div>
    <h3 className="mt-4 text-lg font-semibold text-white">{title}</h3>
    <p className="mt-2 text-sm leading-relaxed text-white/65">{body}</p>
  </div>
);

const WhatToExpect = ({
  c,
  localePath,
}: {
  c: ReturnType<typeof useCopy>;
  localePath: (p: string) => string;
}) => {
  const icons = [
    <FileText key="0" className="h-5 w-5" />,
    <Network key="1" className="h-5 w-5" />,
    <Truck key="2" className="h-5 w-5" />,
    <UserCheck key="3" className="h-5 w-5" />,
  ];
  return (
    <DarkSection>
      <div className="container py-14 md:py-24">
        <SectionHeader eyebrow={c.s2Eyebrow} title={c.s2Title} sub={c.s2Sub} />
        <div className="mt-10 grid gap-4 sm:mt-12 sm:gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {c.s2Cards.map((card, i) => (
            <GlassCard
              key={card.title}
              icon={icons[i] ?? <Boxes className="h-5 w-5" />}
              title={card.title}
              body={card.body}
            />
          ))}
        </div>
        <div className="mt-10 text-center">
          <Button
            asChild
            variant="outline"
            className="group h-11 border-white/15 bg-white/[0.04] text-white transition-colors hover:border-white/25 hover:bg-white/[0.08] hover:text-white"
          >
            <Link
              to={localePath("/ask-switzh")}
              onClick={() => trackCtaClick("home_s2", "ask_switzh")}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {c.ctaAsk}
              <ArrowRight className="cta-arrow ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </DarkSection>
  );
};

/* ─────────────────────  SECTION 3: PROBLEM  ───────────────────── */

const Problem = ({ c }: { c: ReturnType<typeof useCopy> }) => (
  <DarkSection>
    <div className="container py-14 md:py-24">
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-6">
          <SectionHeader eyebrow={c.s3Eyebrow} title={c.s3Title} align="left" />
          <p className="mt-5 text-base leading-relaxed text-white/65 sm:text-lg">
            {c.s3Body}
          </p>
          <p className="mt-6 text-sm leading-relaxed text-white/55">
            {c.s3Foot}
          </p>
        </div>
        <div className="lg:col-span-6">
          <div className="grid gap-2 sm:grid-cols-2">
            {c.s3List.map((item) => (
              <div
                key={item}
                className="flex items-start gap-2.5 rounded-lg border border-white/10 bg-white/[0.03] px-3.5 py-3 text-sm text-white/80"
              >
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#2B63F6]" />
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </DarkSection>
);

/* ─────────────────────  SECTION 4: SOLUTION  ───────────────────── */

const Solution = ({ c }: { c: ReturnType<typeof useCopy> }) => (
  <DarkSection>
    <div className="container py-14 md:py-24">
      <SectionHeader eyebrow={c.s4Eyebrow} title={c.s4Title} sub={c.s4Body} />
      <ol className="mx-auto mt-10 max-w-4xl space-y-2.5 sm:mt-12 sm:space-y-3">
        {c.s4Steps.map((step, i) => (
          <li
            key={step}
            className="card-premium flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 backdrop-blur"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#2B63F6]/40 bg-[#2B63F6]/15 text-sm font-semibold text-[#7DA8FF]">
              {i + 1}
            </span>
            <span className="text-sm leading-relaxed text-white/85 sm:text-base">
              {step}
            </span>
          </li>
        ))}
      </ol>
    </div>
  </DarkSection>
);

/* ────────────────  SECTION 5: WHAT BUYERS CAN SOURCE  ──────────────── */

const WhatToSource = ({ c }: { c: ReturnType<typeof useCopy> }) => (
  <DarkSection>
    <div className="container py-14 md:py-24">
      <SectionHeader eyebrow={c.s5Eyebrow} title={c.s5Title} sub={c.s5Sub} />
      <div className="mx-auto mt-10 grid max-w-6xl gap-4 sm:mt-12 sm:gap-5 md:grid-cols-3">
        {c.s5Categories.map((cat, i) => {
          const [title, body] = cat.split("|");
          const Icon = [Boxes, Network, PackageSearch][i] ?? Zap;
          return (
            <div
              key={title}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur transition hover:border-[#2B63F6]/40 hover:bg-white/[0.05]"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#2B63F6]/15 text-[#7DA8FF] ring-1 ring-inset ring-[#2B63F6]/30">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-white">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/65">{body}</p>
            </div>
          );
        })}
      </div>
      <p className="mx-auto mt-8 max-w-2xl text-center text-sm leading-relaxed text-white/55">
        {c.s5Foot}
      </p>
    </div>
  </DarkSection>
);

/* ─────────────────────  SECTION 6: HOW IT WORKS  ───────────────────── */

const HowItWorks = ({ c, localePath }: { c: ReturnType<typeof useCopy>; localePath: (p: string) => string }) => (
  <DarkSection>
    <div className="container py-14 md:py-24">
      <SectionHeader eyebrow={c.s6Eyebrow} title={c.s6Title} />
      <ol className="mt-10 grid gap-3 sm:mt-12 sm:gap-4 md:grid-cols-4">
        {c.s6Steps.map((step, i) => (
          <li key={step.title} className="relative">
            <div className="card-premium rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full border border-[#2B63F6]/40 bg-[#2B63F6]/15 text-xs font-semibold text-[#7DA8FF]">
                  {i + 1}
                </span>
                <h3 className="text-sm font-semibold text-white">{step.title}</h3>
              </div>
              <p className="mt-2.5 text-[13px] leading-relaxed text-white/65">
                {step.body}
              </p>
            </div>
            {i < c.s6Steps.length - 1 && (
              <div
                aria-hidden
                className="hidden md:block absolute right-[-10px] top-1/2 h-[2px] w-5 -translate-y-1/2 bg-gradient-to-r from-[#2B63F6]/60 to-transparent"
              />
            )}
          </li>
        ))}
      </ol>
      <div className="mt-10 text-center">
        <Button
          asChild
          className="group cta-glow h-11 bg-[#2B63F6] font-semibold text-white hover:bg-[#1F56E0]"
        >
          <Link to={localePath("/ask-switzh")} onClick={() => trackCtaClick("home_s6", "ask_switzh")}>
            <Sparkles className="mr-2 h-4 w-4" />
            {c.s6Cta}
            <ArrowRight className="cta-arrow ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  </DarkSection>
);

/* ─────────────────────  SECTION 7: WHY SWITZH  ───────────────────── */

const WhySwitzh = ({ c }: { c: ReturnType<typeof useCopy> }) => {
  const icons = [Target, Layers, ShieldCheck, Truck, Gauge];
  return (
    <DarkSection>
      <div className="container py-14 md:py-24">
        <SectionHeader eyebrow={c.s7Eyebrow} title={c.s7Title} sub={c.s7Body} />
        <div className="mx-auto mt-10 grid max-w-6xl gap-4 sm:mt-12 sm:gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {c.s7Cards.map((card, i) => {
            const Icon = icons[i] ?? CheckCircle2;
            return (
              <GlassCard
                key={card.title}
                icon={<Icon className="h-5 w-5" />}
                title={card.title}
                body={card.body}
              />
            );
          })}
        </div>
      </div>
    </DarkSection>
  );
};

/* ─────────────────────  SECTION 8: SUPPLIER ANGLE  ───────────────────── */

const SupplierAngle = ({ c, localePath }: { c: ReturnType<typeof useCopy>; localePath: (p: string) => string }) => (
  <DarkSection>
    <div className="container py-14 md:py-24">
      <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-6">
          <SectionHeader eyebrow={c.s8Eyebrow} title={c.s8Title} align="left" />
          <p className="mt-5 text-base leading-relaxed text-white/65 sm:text-lg">
            {c.s8Body}
          </p>
          <Button
            asChild
            className="group cta-glow mt-6 h-11 bg-[#2B63F6] font-semibold text-white hover:bg-[#1F56E0]"
          >
            <Link
              to={localePath("/supplier-partners")}
              onClick={() => trackCtaClick("home_s8", "supplier_partners")}
            >
              {c.s8Cta}
              <ArrowRight className="cta-arrow ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
        <div className="lg:col-span-6">
          <div className="grid gap-2.5 sm:grid-cols-2">
            {c.s8Benefits.map((b) => (
              <div
                key={b}
                className="flex items-start gap-2.5 rounded-lg border border-white/10 bg-white/[0.03] px-3.5 py-3 text-sm text-white/80"
              >
                <Network className="mt-0.5 h-4 w-4 shrink-0 text-[#7DA8FF]" />
                {b}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </DarkSection>
);

/* ─────────────────────  SECTION 9: TRUST  ───────────────────── */

const Trust = ({ c }: { c: ReturnType<typeof useCopy> }) => (
  <DarkSection>
    <div className="container py-14 md:py-24">
      <SectionHeader eyebrow={c.s9Eyebrow} title={c.s9Title} sub={c.s9Body} />
      <div className="mx-auto mt-10 grid max-w-5xl gap-3 sm:mt-12 sm:grid-cols-2 lg:grid-cols-4">
        {c.s9Items.map((item, i) => {
          const Icon = [ShieldCheck, Workflow, FileText, Settings2, Truck, Globe, Network, Layers][i % 8];
          return (
            <div
              key={item}
              className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4"
            >
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#7DA8FF]" />
              <span className="text-sm text-white/80">{item}</span>
            </div>
          );
        })}
      </div>
      <div className="mx-auto mt-10 max-w-3xl text-center">
        <p className="text-base font-medium text-white sm:text-lg">
          {c.s9Callout}
        </p>
      </div>
    </div>
  </DarkSection>
);

/* ─────────────────────  SECTION 10: FINAL CTA  ───────────────────── */

const FinalCta = ({ c, localePath }: { c: ReturnType<typeof useCopy>; localePath: (p: string) => string }) => (
  <DarkSection className="border-t border-white/10">
    <div className="container py-16 pb-32 md:py-28 md:pb-28">
      <div className="relative mx-auto max-w-4xl overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-7 text-center backdrop-blur sm:p-10 md:p-14">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[640px] -translate-x-1/2 rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(43,99,246,0.45), rgba(43,99,246,0) 70%)",
            filter: "blur(24px)",
          }}
        />
        <div className="relative">
          <Eyebrow>{c.s10Eyebrow}</Eyebrow>
          <h2 className="mt-5 text-[28px] font-semibold leading-[1.15] tracking-[-0.02em] text-white sm:text-4xl lg:text-5xl">
            {c.s10Title}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
            {c.s10Body}
          </p>
          <div className="mt-7 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <Button
              asChild
              size="lg"
              className="group cta-glow h-12 bg-[#2B63F6] font-semibold text-white shadow-[0_8px_30px_-8px_rgba(43,99,246,0.7)] hover:bg-[#1F56E0]"
            >
              <Link to={localePath("/ask-switzh")} onClick={() => trackCtaClick("home_final", "ask_switzh")}>
                <Sparkles className="mr-2 h-4 w-4" />
                {c.ctaAsk}
                <ArrowRight className="cta-arrow ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="group h-12 border-white/15 bg-white/[0.04] font-semibold text-white transition-colors hover:border-white/25 hover:bg-white/[0.08] hover:text-white"
            >
              <Link to={localePath("/request-quote")} onClick={() => trackCtaClick("home_final", "submit_rfq")}>
                <Upload className="mr-2 h-4 w-4" />
                {c.ctaRfq}
              </Link>
            </Button>
          </div>
          <p className="mt-5 text-xs leading-relaxed text-white/50">
            {c.s10Trust}
          </p>
          <Link
            to={localePath("/supplier-partners")}
            onClick={() => trackCtaClick("home_final", "supplier_partners")}
            className="cta-arrow-host mt-4 inline-flex items-center text-sm font-medium text-[#7DA8FF] transition-colors hover:text-white"
          >
            {c.s10Tertiary} <ArrowRight className="cta-arrow ml-1 h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  </DarkSection>
);

/* ─────────────────────  ROOT  ───────────────────── */

function useCopy() {
  const { lang } = useLang();
  return useMemo(() => HOME_COPY[lang] ?? HOME_COPY.en, [lang]);
}

const HomepageRedesign = () => {
  const { localePath } = useLang();
  const c = useCopy();
  return (
    <>
      <Hero c={c} localePath={localePath} />
      <Reveal><WhatToExpect c={c} localePath={localePath} /></Reveal>
      <Reveal><Problem c={c} /></Reveal>
      <Reveal><Solution c={c} /></Reveal>
      <Reveal><WhatToSource c={c} /></Reveal>
      <Reveal><WhySwitzh c={c} /></Reveal>
      <Reveal><SupplierAngle c={c} localePath={localePath} /></Reveal>
      <Reveal><Trust c={c} /></Reveal>
      <Reveal><FinalCta c={c} localePath={localePath} /></Reveal>
    </>
  );
};

export default HomepageRedesign;
